import { EnvironmentOutlined } from '@ant-design/icons';
import { Form, FormInstance, Input } from 'antd';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  FieldLabel,
  FieldWrapper,
  MissingFieldsNote,
  SelectedAddressLabel,
  Wrapper
} from './AddressFieldsSection.styles';
import { useAddressFields } from './hooks/useAddressFields';

import {
  AddressAutocomplete,
  ProvincesExtendedSelector,
  StreetTypeSelector
} from '@/components/Form';

interface Props {
  form: FormInstance;
}

export const AddressFieldsSection: React.FC<Props> = ({ form }) => {
  const { t } = useTranslation('DefaultTranslations');
  const wrapperRef = useRef<HTMLDivElement>(null);

  const {
    fullAddress,
    initialAddressForAutocomplete,
    setAutocompleteError,
    handleAddressSelect,
    handleAddressClear,
    hasSelectedAddress,
    hasMissingFields,
    shouldShowAllFields,
    shouldShowField
  } = useAddressFields({ form, wrapperRef });

  return (
    <Wrapper ref={wrapperRef}>
      <Form.Item
        name={['userInfo', 'address']}
        hidden
        rules={[{ required: true, message: t('REQUIRED_FIELD') }]}>
        <Input />
      </Form.Item>
      <Form.Item name={['userInfo', 'province']} hidden>
        <Input />
      </Form.Item>
      <Form.Item name={['userInfo', 'city']} hidden>
        <Input />
      </Form.Item>
      <Form.Item name={['userInfo', 'residenceLocality']} hidden>
        <Input />
      </Form.Item>
      <Form.Item name={['userInfo', 'streetType']} hidden>
        <Input />
      </Form.Item>
      <Form.Item name={['userInfo', 'streetNumber']} hidden>
        <Input />
      </Form.Item>
      <Form.Item name={['userInfo', 'postalCode']} hidden>
        <Input />
      </Form.Item>
      <Form.Item name={['userInfo', 'countryResidence']} hidden>
        <Input />
      </Form.Item>
      <FieldWrapper>
        <FieldLabel>{t('SEARCH_ADDRESS')}</FieldLabel>
        <AddressAutocomplete
          initialValue={initialAddressForAutocomplete}
          onAddressSelect={handleAddressSelect}
          onClear={handleAddressClear}
          placeholder={t('SEARCH_ADDRESS')}
          onError={setAutocompleteError}
        />
      </FieldWrapper>

      {(hasSelectedAddress || shouldShowAllFields) && (
        <>
          {hasSelectedAddress && (
            <SelectedAddressLabel>
              <EnvironmentOutlined className="address-icon" />
              <span className="address-text">{fullAddress}</span>
            </SelectedAddressLabel>
          )}

          {shouldShowAllFields && (
            <MissingFieldsNote style={{ color: '#ff4d4f' }}>
              {t('ADDRESS_AUTOCOMPLETE_ERROR')}
            </MissingFieldsNote>
          )}

          {hasMissingFields && !shouldShowAllFields && (
            <MissingFieldsNote>{t('COMPLETE_MISSING_ADDRESS_FIELDS')}</MissingFieldsNote>
          )}

          {shouldShowField('province') && (
            <FieldWrapper>
              <FieldLabel>
                <span className="required">*</span> {t('PROVINCE')}
              </FieldLabel>
              <Form.Item
                rules={[
                  {
                    required: true,
                    message: t('REQUIRED_PROVINCE')
                  }
                ]}
                name={['userInfo', 'province']}>
                <ProvincesExtendedSelector />
              </Form.Item>
            </FieldWrapper>
          )}

          {shouldShowField('city') && (
            <FieldWrapper>
              <FieldLabel>
                <span className="required">*</span> {t('MUNICIPALITY')}
              </FieldLabel>
              <Form.Item
                rules={[
                  {
                    required: true,
                    message: t('REQUIRED_MUNICIPALITY')
                  }
                ]}
                name={['userInfo', 'city']}>
                <Input />
              </Form.Item>
            </FieldWrapper>
          )}

          {shouldShowField('residenceLocality') && (
            <FieldWrapper>
              <FieldLabel>
                <span className="required">*</span> {t('LOCALITY')}
              </FieldLabel>
              <Form.Item
                rules={[
                  {
                    required: true,
                    message: t('REQUIRED_LOCALITY')
                  }
                ]}
                name={['userInfo', 'residenceLocality']}>
                <Input />
              </Form.Item>
            </FieldWrapper>
          )}
          {shouldShowField('streetType') && (
            <FieldWrapper>
              <FieldLabel>
                <span className="required">*</span> {t('STREET_TYPE')}
              </FieldLabel>
              <Form.Item
                rules={[
                  {
                    required: true,
                    message: t('REQUIRED_STREET_TYPE')
                  }
                ]}
                name={['userInfo', 'streetType']}>
                <StreetTypeSelector />
              </Form.Item>
            </FieldWrapper>
          )}

          {shouldShowField('address') && (
            <FieldWrapper>
              <FieldLabel>
                <span className="required">*</span> {t('STREET_NAME')}
              </FieldLabel>
              <Form.Item name={['userInfo', 'address']}>
                <Input />
              </Form.Item>
            </FieldWrapper>
          )}

          {shouldShowField('streetNumber') && (
            <FieldWrapper>
              <FieldLabel>
                <span className="required">*</span> {t('STREET_NUMBER')}
              </FieldLabel>
              <Form.Item
                rules={[
                  {
                    required: true,
                    message: t('REQUIRED_STREET_NUMBER')
                  }
                ]}
                name={['userInfo', 'streetNumber']}>
                <Input />
              </Form.Item>
            </FieldWrapper>
          )}

          {shouldShowField('postalCode') && (
            <FieldWrapper>
              <FieldLabel>
                <span className="required">*</span> {t('POSTAL_CODE')}
              </FieldLabel>
              <Form.Item
                rules={[
                  {
                    required: true,
                    message: t('REQUIRED_POSTAL_CODE')
                  }
                ]}
                name={['userInfo', 'postalCode']}>
                <Input />
              </Form.Item>
            </FieldWrapper>
          )}

          <FieldWrapper>
            <FieldLabel>{t('STAIRCASE')}</FieldLabel>
            <Form.Item name={['userInfo', 'staircase']}>
              <Input />
            </Form.Item>
          </FieldWrapper>

          <FieldWrapper>
            <FieldLabel>{t('FLOOR')}</FieldLabel>
            <Form.Item name={['userInfo', 'floor']}>
              <Input />
            </Form.Item>
          </FieldWrapper>

          <FieldWrapper>
            <FieldLabel>{t('DOOR')}</FieldLabel>
            <Form.Item name={['userInfo', 'door']}>
              <Input />
            </Form.Item>
          </FieldWrapper>
        </>
      )}
    </Wrapper>
  );
};
